import MuiButton, { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import React, { forwardRef, FunctionComponent, KeyboardEventHandler, useRef } from "react";
import { mergeRefs } from "react-merge-refs";
import { useLocale } from "designSystem/locale";
import { mergeSx } from "designSystem/styles/style-functions";
import { blendButtonStyle, darkModeButtonStyle, darkModePrimaryButtonStyle, defaultButtonStyle, defaultOnGreyButtonStyle, linkButtonStyle, linkSubtleButtonStyle, outlineButtonStyle, primaryStyle, secondaryStyle } from "designSystem/styles/style-functions";
import Tooltip, { TooltipProps } from "../tooltip/Tooltip";

const ENTER_KEY = "Enter";
const hideOverflowClass = "ButtonHideOverflow";

const colorMapping = {
	blend: blendButtonStyle,
	darkMode: darkModeButtonStyle,
	darkModePrimary: darkModePrimaryButtonStyle,
	defaultOnGrey: defaultOnGreyButtonStyle,
	link: linkButtonStyle,
	linkSubtle: linkSubtleButtonStyle,
	outline: outlineButtonStyle,
	primary: primaryStyle,
	secondary: secondaryStyle,
};

const rootStyles = (props: ButtonProps, theme) => {
	const { children, endIcon, fullWidth, res, size, startIcon } = props;
	const styleFunction = colorMapping[props.design] || defaultButtonStyle;
	const styles = styleFunction ? styleFunction(theme) : null;
	const hasText = res || children;
	const isSmall = size === "small";
	let paddingLeft: string | number;
	let paddingRight: string | number;

	if (!fullWidth) {
		if (startIcon) {
			paddingLeft = isSmall ? "1px" : "4px";
			paddingRight = hasText ? null : 0;
		} else if (endIcon) {
			paddingLeft = hasText ? null : 0;
			paddingRight = isSmall ? "1px" : "4px";
		}
	}

	return {
		paddingLeft,
		paddingRight,
		["&.MuiButton-iconSizeSmall"]: iconSizeSmall(props),
		[`&.${hideOverflowClass}`]: {
			margin: theme.spacing(1),
			overflow: "hidden",
		},
		...styles,
	};
};

const iconSizeSmall = (props: ButtonProps) => {
	const { children, endIcon, res, size, startIcon } = props;

	if (!res && !children) {
		if (startIcon) {
			return {
				marginRight: 1,
			};
		}

		if (endIcon) {
			return {
				marginLeft: 1,
			};
		}
	}

	return null;
};

export interface ButtonProps extends Omit<MuiButtonProps, "variant"> {
	/** design of button (default has no implied color/border) */
	design?:
	| "blend"
	| "darkMode"
	| "darkModePrimary"
	| "default"
	| "defaultOnGrey"
	| "link"
	| "linkSubtle"
	| "outline"
	| "primary"
	| "secondary";

	/** enable if using OverflowText to truncate text (note that this will add some margin to button to allow for box-shadow on focus) */
	hideOverflow?: boolean;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** properties for tooltip */
	tooltipProps?: Partial<TooltipProps>;
};

const Button: FunctionComponent<ButtonProps> = forwardRef((props, ref) => {
	const {
		children,
		className,
		design,
		hideOverflow,
		onClick,
		onKeyDown,
		onKeyUp,
		res,
		resParams,
		sx,
		title,
		tooltipProps,
		...otherProps
	} = props;

	const dataCsiAutomation= res ? `button-${res}` : undefined;
	const locale = useLocale();
	const text = res ? locale.resource(res, resParams) : null;
	const buttonRef = useRef<HTMLButtonElement>(null);
	const enterOnKeyUp = !onKeyDown && !onKeyUp;
	const theme = useTheme();

	// if component has no keyboard handling, then switch conversion of ENTER key to onClick to onKeyUp rather than onKeyDown
	// to be consistent with Space key and keyboard handling in Textfield
	const _onKeyDown: KeyboardEventHandler = (event) => {
		if (event.key === ENTER_KEY) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const _onKeyUp: KeyboardEventHandler = (event) => {
		if (event.key === ENTER_KEY) {
			event.stopPropagation();
			buttonRef.current.click();
		}
	};

	const button = (
		<MuiButton
			className={`${className || ""} ${hideOverflow ? hideOverflowClass : ""}`}
			data-csi-automation={dataCsiAutomation}
			data-testid="Button"
			onClick={onClick}
			onKeyDown={enterOnKeyUp ? _onKeyDown : onKeyDown}
			onKeyUp={enterOnKeyUp ? _onKeyUp : onKeyUp}
			ref={mergeRefs([buttonRef, ref])}
			sx={mergeSx(sx, rootStyles(props, theme))}
			{...otherProps}
		>
			{text || children}
		</MuiButton>
	);

	const tooltipTitle = title || tooltipProps?.title;

	return tooltipTitle || tooltipProps?.res ? (
		<Tooltip {...tooltipProps} title={tooltipTitle}>
			{button}
		</Tooltip>
	)
		:
		button;
});

export default Button;